import {createApiResource} from 'data/utils';
import Cookies from 'js-cookie';
import {useQuery} from '@tanstack/react-query';
import {useApi} from 'hooks/api/useApi';
import {productQueryClientHelpers} from 'data/product';

const resource = createApiResource({
	name: 'organization',
	pathName: '/organizations',
	cascadeTo: ['organizationOptions'],
});

export const organizationQueryClientHelpers = resource.createQueryClientHelpers();

/**
 * Helper function to read the current organization ID from cookie
 * @returns {number|null} The organization ID as a number, or null if not found
 */
export const getCurrentOrganizationIdCookie = () => {
	const orgId = Cookies.get('organization_id');
	return orgId ? Number(orgId) : null;
};

export const useOrganizations = resource.createUseQueryIndex();
export const useOrganization = resource.createUseQueryById();

export const useCurrentOrganization = () => {
	const {get} = useApi();

	return useQuery({
		queryKey: ['organization', 'current'],
		queryFn: async () => {
			const currentOrgId = getCurrentOrganizationIdCookie();

			if (!currentOrgId) {
				throw new Error('No organization ID available');
			}

			return get(resource.getResourcePath(currentOrgId)).then(resource.unwrap);
		},
		enabled: !!getCurrentOrganizationIdCookie(),
		refetchOnWindowFocus: false,
		staleTime: Infinity,
	});
};

const useOnSettledOrganizationUpdate = ({id}, {queryClient}) => {
	const currentOrgId = getCurrentOrganizationIdCookie();

	if (id === currentOrgId) {
		organizationQueryClientHelpers.invalidateCustom(queryClient, 'current');
	}

	// Invalidate products cache since organization-product relationships may have changed
	productQueryClientHelpers.invalidateIndex(queryClient);
};

export const useCreateOrganization = resource.createUseMutationCreate();
export const useUpdateOrganization = resource.createUseMutationUpdate({
	onSettled: useOnSettledOrganizationUpdate,
});
export const useDeleteOrganization = resource.createUseMutationDelete();

/**
 * Direct helper function for setting current organization data (for use outside of components)
 * @param {Object} queryClient - React Query client instance
 * @param {Object} organizationData - Organization data to set in cache
 */
export const setCurrentOrganizationQueryData = (queryClient, organizationData) => {
	organizationQueryClientHelpers.setCustomData(
		queryClient,
		'current',
		{},
		organizationData,
	);
};
